// ABOUTME: Presentation-layer guard for job failure text (#2201). Backend
// error_message fields are a mixed bag: some are sentences written for the
// owner ("Facebook rejected the image"), others are raw exception reprs that
// leaked through a fail-soft path ("'Run' object has no attribute 'get'").
// The jobs panel must never show the latter verbatim — this classifies the
// string by shape (not by matching any one known message) and swaps
// machine-facing text for friendly copy, keeping the raw string available as
// a secondary detail (tooltip) rather than primary UI.

/**
 * Shapes that identify machine-facing exception text. Pattern-based on
 * purpose: special-casing individual known strings would miss the next
 * exception class to leak.
 */
const INTERNAL_ERROR_PATTERNS: RegExp[] = [
  // Python attribute/type failures: 'Run' object has no attribute 'get'
  /object has no attribute/i,
  /object is not (?:subscriptable|iterable|callable)/i,
  // Exception class names, bare or with a message: KeyError: 'draft_id'
  /\b\w*(?:Error|Exception)\b\s*[:(]?/,
  /\bTraceback\b/i,
  /\bNoneType\b/,
  // Python file locations: File "/srv/app/x.py", line 42
  /File "[^"]+", line \d+/,
  // HTTP client dumps: 500 Internal Server Error / status_code=502
  /status[_ ]?code[=:]\s*\d{3}/i,
]

export interface JobFailureCopy {
  /** What the panel shows — always owner-facing prose. */
  message: string
  /** The raw technical string when it was suppressed; null when `message` IS the original. */
  detail: string | null
}

/** Friendly primary line + optional technical detail for a failed job. */
export function describeJobFailure(errorMessage: string | null | undefined): JobFailureCopy | null {
  const raw = errorMessage?.trim()
  if (!raw) return null
  if (INTERNAL_ERROR_PATTERNS.some((p) => p.test(raw))) {
    return { message: "Hit a technical problem and couldn't finish.", detail: raw }
  }
  return { message: raw, detail: null }
}
